import { Suspense, type ReactNode } from 'react';
import { lazyWithRetry as lazy } from '@/lib/lazyWithRetry';
import { Toaster as Sonner } from "@/components/ui/sonner";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";
import { setChatStoreQueryClient } from "@/stores/chat-store.query-client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { PrivateRoute } from "@/components/PrivateRoute";
import { PageLoader } from "@/components/ui/page-loader";
import ErrorBoundary from "@/components/ErrorBoundary";

// ============================================================================
// FORA DO CHUNK INICIAL (spec `crm-alvo-v2-fechamento`, fatia 6)
// ============================================================================
// Tudo abaixo e' `lazy` de ARQUIVO (nunca de barril — lazy de barril ja deu
// React #306). Nada disso e' necessario para o primeiro paint:
//
//  - `AppSection`: o miolo de `/app` (assinatura + banner de carencia, tooltips
//    do Radix com @floating-ui, VoIP/Vapi e o shell). Quem nao esta logado e'
//    redirecionado pelo `PrivateRoute` sem nunca baixar esse chunk.
//  - `CrmRouteGate`: so roda dentro do shell (que ja tem `Suspense` em volta do
//    `<Outlet/>`); leva junto `useTeam` + `services/team`.
//  - `Toaster` (Radix toast): o estado dos toasts vive no modulo
//    `hooks/use-toast` (`memoryState`), entao um `toast()` disparado antes do
//    chunk chegar fica na fila e aparece quando o `Toaster` monta. Ele e'
//    renderizado ja no primeiro render, entao o download comeca no boot, em
//    paralelo com a rota.
//
// O `Sonner` fica eager: o pacote ja esta no bucket `vendor-app-core` (fora do
// `index-*.js`) e nao ha garantia de que o `toast()` dele enfileire antes do
// `<Toaster>` montar — a maior parte dos toasts do app passa por ele.
const AppSection = lazy(() => import("@/components/layout/AppSection"));
const CrmRouteGate = lazy(() =>
  import("@/components/common/CrmRouteGate").then((m) => ({ default: m.CrmRouteGate })),
);
const Toaster = lazy(() =>
  import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })),
);

// ============================================================================
// LAZY LOADING DE PAGINAS
// ============================================================================
// Todas as paginas sao carregadas sob demanda para reduzir o bundle inicial.
// Isso melhora significativamente o Time to Interactive (TTI) e First Contentful Paint (FCP).

// Paginas publicas
const LandingPage = lazy(() => import("./pages/public/LandingPage"));
const Auth = lazy(() => import("./pages/auth/Auth"));
// F3-13 — recuperacao de senha (o backend ja expunha forgot/reset e ninguem
// consumia). `/reset-password` e exatamente o caminho que o api-core monta no
// e-mail (`${frontendUrl}/reset-password?token=...`): renomear esta rota
// invalida todo link ja enviado.
const ForgotPasswordPage = lazy(() => import("./pages/auth/ForgotPasswordPage"));
// crm-alvo-v2-fechamento FA — retorno do login Google (?code= / ?error=)
const GoogleCallbackPage = lazy(() => import("./pages/auth/GoogleCallbackPage"));
const ResetPasswordPage = lazy(() => import("./pages/auth/ResetPasswordPage"));
const NotFound = lazy(() => import("./pages/errors/NotFound"));
// B7 — pagina publica de assinatura de proposta (token, sem auth)
const SignProposal = lazy(() => import("./pages/public/SignProposal"));

// Paginas de onboarding (publicas - sem PrivateRoute)
const SelectPlanPage = lazy(() => import("./pages/onboarding/SelectPlanPage"));
const OnboardingSuccessPage = lazy(() => import("./pages/onboarding/OnboardingSuccessPage"));
const InviteCodePage = lazy(() => import("./pages/onboarding/InviteCodePage"));

// Paginas do App (usuario autenticado)
// Story 02 (redesign-crm): `/app` e a tela "Hoje"; a grade de widgets
// (`AppDashboard`) continua inteira, com as preferencias do usuario, em
// `/app/painel`.
const TodayPage = lazy(() => import("./pages/app/home/TodayPage"));
const Dashboard = lazy(() => import("./pages/app/dashboard/Dashboard"));
const Pipeline = lazy(() => import("./pages/app/pipeline/Pipeline"));
const Leads = lazy(() => import("./pages/app/leads/Leads"));
const LeadDetails = lazy(() => import("./pages/app/leads/LeadDetails"));
const LeadScoring = lazy(() => import("./pages/app/lead-scoring/LeadScoring"));
const Sequences = lazy(() => import("./pages/app/sequences/Sequences"));
const SequenceDetail = lazy(() => import("./pages/app/sequences/SequenceDetail"));
const CompanySearch = lazy(() => import("./pages/app/prospecting/CompanySearch"));
const WorkflowsPage = lazy(() => import("./pages/app/workflows/WorkflowsPage").then(m => ({ default: m.WorkflowsPage })));
const WorkflowEditorPage = lazy(() => import("./pages/app/workflows/WorkflowEditorPage").then(m => ({ default: m.WorkflowEditorPage })));
const AIAgents = lazy(() => import("./pages/app/ai/agents/AIAgents"));
const AIAnalytics = lazy(() => import("./pages/app/ai/analytics/AIAnalytics"));
const ChatHubPage = lazy(() => import("./pages/app/ai/chat/ChatHubPage"));
const AIAgentChat = lazy(() => import("./pages/app/ai/chat/AIAgentChat"));
const AIMarketplace = lazy(() => import("./pages/app/ai/marketplace/AIMarketplace"));
const WhatsAppChat = lazy(() => import("./pages/app/contacts/whatsapp/WhatsAppPage"));
const WhatsAppNumbers = lazy(() => import("./pages/app/contacts/whatsapp/WhatsAppNumbers"));
const WhatsAppTemplates = lazy(() => import("./pages/app/contacts/whatsapp/WhatsAppTemplates"));
const WhatsAppGallery = lazy(() => import("./pages/app/contacts/whatsapp/WhatsAppGallery"));
const EmailPage = lazy(() => import("./pages/app/contacts/email/EmailPage"));
const CalendarPage = lazy(() => import("./pages/app/calendar/Calendar"));
const TasksPage = lazy(() => import("./pages/app/tasks/TasksPage"));
const CredentialsPage = lazy(() => import("./pages/app/credentials/CredentialsPage"));
const WebhooksPage = lazy(() => import("./pages/app/webhooks/WebhooksPage"));
const AuditLogsPage = lazy(() => import("./pages/app/audit/AuditLogsPage"));
const Reports = lazy(() => import("./pages/app/reports/Reports"));
const AnalyticsPage = lazy(() => import("./pages/app/analytics/AnalyticsPage"));
const Settings = lazy(() => import("./pages/app/settings/Settings"));
const Profile = lazy(() => import("./pages/app/profile/Profile"));
const SubscriptionPage = lazy(() => import("./pages/app/subscription/SubscriptionPage"));
const Customers = lazy(() => import("./pages/app/customers/Customers"));
// Story 08 (redesign-crm) — as duas rotas aditivas do hub Clientes:
// `/app/contatos` (pessoas, projetadas sobre os leads) e `/app/importar` (o
// assistente de importacao em tela cheia, sobre os MESMOS passos do dialogo).
const ContactsPage = lazy(() => import("./pages/app/contacts/people/ContactsPage"));
const ImportPage = lazy(() => import("./pages/app/import/ImportPage"));
const CustomerDetails = lazy(() => import("./pages/app/customers/CustomerDetails"));
const Products = lazy(() => import("./pages/app/products/Products"));
const ExpiringContractsPage = lazy(() => import("./pages/app/contracts/ExpiringContractsPage"));
const ProductDetails = lazy(() => import("./pages/app/products/ProductDetails"));
const VoiceAgents = lazy(() => import("./pages/app/voice-agents/VoiceAgents"));
const VoiceConfigEditPage = lazy(() => import("./pages/app/voice-agents/VoiceConfigEditPage"));
const PhoneNumbers = lazy(() => import("./pages/app/phone-numbers/PhoneNumbers"));
const CallHistory = lazy(() => import("./pages/app/voice-agents/CallHistory"));
const CallCenter = lazy(() => import("./pages/app/voip/CallCenter"));
const CreateAgentPage = lazy(() => import("./pages/app/ai/agents/CreateAgentPage"));
const CreateGroupPage = lazy(() => import("./pages/app/ai/agents/CreateGroupPage"));
const AgentConfigPage = lazy(() => import("./pages/app/ai/agents/AgentConfigPage"));
const AgentGroupConfigPage = lazy(() => import("./pages/app/ai/groups/AgentGroupConfigPage"));
const KnowledgeBases = lazy(() => import("./pages/app/knowledge/KnowledgeBases"));
const KnowledgeDetailPage = lazy(() => import("./pages/app/knowledge/KnowledgeDetailPage"));
const ToolsPage = lazy(() => import("./pages/app/tools/ToolsPage"));
const TokensPage = lazy(() => import("./pages/app/tokens/TokensPage"));
const UsagePage = lazy(() => import("./pages/app/usage/UsagePage"));
const NotificationsPage = lazy(() => import("./pages/app/notifications/NotificationsPage"));
const TeamPage = lazy(() => import("./pages/app/team/TeamPage"));
const MemberDetailPage = lazy(() => import("./pages/app/team/MemberDetailPage"));
const RolesPage = lazy(() => import("./pages/app/team/RolesPage"));
const ActivitiesPage = lazy(() => import("./pages/app/activities/ActivitiesPage"));
const InboundAgentPage = lazy(() => import("./pages/app/inbound-agent/InboundAgentPage"));
const AssistantCrmPage = lazy(() => import("./pages/app/assistant-crm/AssistantCrmPage"));
const InboxPage = lazy(() => import("./pages/app/inbox/InboxPage"));
const SmsPage = lazy(() => import("./pages/app/sms/SmsPage"));
const ChannelsPage = lazy(() => import("./pages/app/channels/ChannelsPage"));

// Agent Missions
const AgentMissions = lazy(() => import("./pages/app/agent-missions/AgentMissions"));
const AgentMissionCreate = lazy(() => import("./pages/app/agent-missions/AgentMissionCreate"));
const AgentMissionDetail = lazy(() => import("./pages/app/agent-missions/AgentMissionDetail"));
const AgentMissionRunDetail = lazy(() => import("./pages/app/agent-missions/AgentMissionRunDetail"));
const AgentMissionPipelineDetail = lazy(() => import("./pages/app/agent-missions/AgentMissionPipelineDetail"));
const AgentMissionSandbox = lazy(() => import("./pages/app/agent-missions/AgentMissionSandbox"));

// ============================================================================
// CONFIGURACAO DO QUERY CLIENT — instancia unica em `lib/query-client.ts`
// (o harness do tutorial limpa o cache entre telas).
// ============================================================================

// Wire up the query client so chat.store can invalidate token balance queries
setChatStoreQueryClient(queryClient);

interface CrmAccessOptions {
  feature?: string;
  permission?: string;
}

function crmPage(sectionName: string, children: ReactNode, access?: CrmAccessOptions) {
  return (
    <CrmRouteGate feature={access?.feature} permission={access?.permission}>
      <ErrorBoundary sectionName={sectionName}>
        {children}
      </ErrorBoundary>
    </CrmRouteGate>
  );
}

// Section-level boundary for public / onboarding routes so one page crashing
// does not take down the whole app (the authenticated /app subtree already
// gets per-page boundaries via crmPage()).
function publicPage(sectionName: Readonly<string>, children: ReactNode) {
  return <ErrorBoundary sectionName={sectionName}>{children}</ErrorBoundary>;
}

// ============================================================================
// COMPONENTE APP
// ============================================================================
const App = () => (
  <ErrorBoundary sectionName="App Root">
    <QueryClientProvider client={queryClient}>
      {/* Toaster sem chunk nao pode derrubar o app: sem ele os toasts so nao
          aparecem (o `ErrorBoundary` reporta a falha). Elemento oculto de
          proposito — `fallback` nulo cairia no ErrorFallback de tela cheia. */}
      <ErrorBoundary sectionName="Toaster" fallback={<span hidden />}>
        <Suspense fallback={null}>
          <Toaster />
        </Suspense>
      </ErrorBoundary>
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={publicPage("Landing", <LandingPage />)} />
            <Route path="/auth" element={publicPage("Auth", <Auth />)} />
            <Route
              path="/auth/google/callback"
              element={publicPage("Google Callback", <GoogleCallbackPage />)}
            />
            <Route
              path="/forgot-password"
              element={publicPage("Forgot Password", <ForgotPasswordPage />)}
            />
            <Route
              path="/reset-password"
              element={publicPage("Reset Password", <ResetPasswordPage />)}
            />
            {/* B7 — Public proposal e-signature page (token-protected, no auth, outside /app) */}
            <Route path="/p/assinar/:token" element={publicPage("Sign Proposal", <SignProposal />)} />
            {/* Onboarding Routes - Plan selection and payment confirmation */}
            <Route path="/onboarding/invite-code" element={publicPage("Invite Code", <InviteCodePage />)} />
            <Route path="/onboarding/select-plan" element={publicPage("Select Plan", <SelectPlanPage />)} />
            <Route path="/onboarding/success" element={publicPage("Onboarding Success", <OnboardingSuccessPage />)} />
            {/* Client/SDR Routes - Protected for authenticated users */}
            <Route
              path="/app"
              element={
                <PrivateRoute>
                  <AppSection />
                </PrivateRoute>
              }
            >
              <Route index element={crmPage("Home", <TodayPage />, { permission: 'canViewDashboard' })} />
              {/* Mesmo gate de `/app`: a grade de widgets mudou de endereco,
                  nao de permissao. */}
              <Route path="painel" element={crmPage("Dashboard", <Dashboard />, { permission: 'canViewDashboard' })} />
              <Route path="pipeline" element={crmPage("Pipeline", <Pipeline />, { permission: 'canViewPipeline' })} />
              <Route path="leads" element={crmPage("Leads", <Leads />, { permission: 'canViewLeads' })} />
              <Route path="leads/:id" element={crmPage("Lead Details", <LeadDetails />, { permission: 'canViewLeads' })} />
              <Route path="lead-scoring" element={crmPage("Lead Scoring", <LeadScoring />, { permission: 'canViewLeads' })} />
              <Route path="sequencias" element={
                crmPage("Sequences", <Sequences />, { feature: 'crm_show_sequences', permission: 'canViewLeads' })
              } />
              <Route path="sequencias/:id" element={
                crmPage("Sequence Detail", <SequenceDetail />, { feature: 'crm_show_sequences', permission: 'canViewLeads' })
              } />
              <Route path="atividades" element={crmPage("Activities", <ActivitiesPage />, { permission: 'canViewLeads' })} />
              <Route path="busca" element={
                crmPage("Company Search", <CompanySearch />, { feature: 'crm_show_prospecting', permission: 'canViewLeads' })
              } />
              <Route path="automacoes" element={
                crmPage("Workflows", <WorkflowsPage />, { feature: 'crm_show_automations', permission: 'canManageAutomations' })
              } />
              <Route path="automacoes/novo" element={
                crmPage("Workflow Editor", <WorkflowEditorPage />, { feature: 'crm_show_automations', permission: 'canManageAutomations' })
              } />
              <Route path="automacoes/:id" element={
                crmPage("Workflow Editor", <WorkflowEditorPage />, { feature: 'crm_show_automations', permission: 'canManageAutomations' })
              } />
              <Route path="missoes-agentes" element={
                crmPage("Agent Missions", <AgentMissions />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="missoes-agentes/nova" element={
                crmPage("Agent Mission Create", <AgentMissionCreate />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="missoes-agentes/:id" element={
                crmPage("Agent Mission Detail", <AgentMissionDetail />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="missoes-agentes/:id/pipelines/:pipelineId" element={
                crmPage("Mission Pipeline", <AgentMissionPipelineDetail />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="missoes-agentes/:id/runs/:runId" element={
                crmPage("Agent Mission Run", <AgentMissionRunDetail />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="missoes-agentes/:id/sandbox" element={
                crmPage("Mission Sandbox", <AgentMissionSandbox />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="agentes-ia" element={
                crmPage("AI Agents", <AIAgents />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="agentes-ia/criar" element={
                crmPage("Create Agent", <CreateAgentPage />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="agentes-ia/criar-grupo" element={
                crmPage("Create Group", <CreateGroupPage />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="agentes-ia/:agentId/config" element={
                crmPage("Agent Config", <AgentConfigPage />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              {/* Story 10 (redesign-crm) — rota aditiva. A lista de grupos
                  era a aba `groups` de `AIAgents` e nao tinha endereco
                  proprio: o item "Grupos de agentes" do painel nao teria
                  para onde apontar, e recarregar a pagina caia de volta na
                  aba de agentes. Mesmo componente, mesmo gate. */}
              <Route path="agentes-ia/grupos" element={
                crmPage("Agent Groups", <AIAgents />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="agentes-ia/grupos/:groupId" element={
                crmPage("Group Config", <AgentGroupConfigPage />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="analytics-ia" element={
                crmPage("AI Analytics", <AIAnalytics />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="chat-ia" element={
                crmPage("Chat Hub", <ChatHubPage />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="agentes-ia/chat/:agentId" element={
                crmPage("Agent Chat", <AIAgentChat />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="marketplace" element={
                crmPage("Marketplace", <AIMarketplace />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="bases-conhecimento" element={
                crmPage("Knowledge Bases", <KnowledgeBases />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="bases-conhecimento/:id" element={
                crmPage("Knowledge Detail", <KnowledgeDetailPage />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="ferramentas" element={
                crmPage("Tools", <ToolsPage />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="inbound-agent" element={
                crmPage("Inbound Auto-Reply", <InboundAgentPage />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              <Route path="assistant-crm" element={
                crmPage("Assistant CRM", <AssistantCrmPage />, { feature: 'crm_show_ai', permission: 'canManageAI' })
              } />
              {/* F1-14: a API do inbox exige canViewLeads (as conversas sao
                  escopadas por lead). A rota pedia canViewContacts, entao quem
                  tinha um e nao o outro abria a pagina e tomava 403 em tudo.
                  Alinhado a exigencia do backend, que e quem guarda o dado. */}
              <Route path="inbox" element={
                crmPage("Team Inbox", <InboxPage />, { permission: 'canViewLeads' })
              } />
              <Route path="sms" element={
                crmPage("SMS", <SmsPage />, { feature: 'crm_show_sms', permission: 'canViewContacts' })
              } />
              <Route path="contatos/whatsapp" element={
                crmPage("WhatsApp", <WhatsAppChat />, { feature: 'crm_show_whatsapp', permission: 'canViewContacts' })
              } />
              <Route path="contatos/whatsapp/numeros" element={
                crmPage("WhatsApp Numbers", <WhatsAppNumbers />, { feature: 'crm_show_whatsapp', permission: 'canViewContacts' })
              } />
              <Route path="contatos/whatsapp/templates" element={
                crmPage("WhatsApp Templates", <WhatsAppTemplates />, { feature: 'crm_show_whatsapp', permission: 'canViewContacts' })
              } />
              <Route path="contatos/whatsapp/galeria" element={
                crmPage("WhatsApp Gallery", <WhatsAppGallery />, { feature: 'crm_show_whatsapp', permission: 'canViewContacts' })
              } />
              <Route path="contatos/email" element={
                crmPage("Email", <EmailPage />, { permission: 'canViewContacts' })
              } />
              {/* Story 06 (redesign-crm) — visao unica dos canais conectados.
                  `canManageIntegrations` e' a permissao de quem conecta canal
                  (a mesma de credenciais e webhooks); a secao `channels` das
                  Configuracoes continua existindo e renderiza o MESMO
                  componente. */}
              <Route path="canais" element={
                crmPage("Channels", <ChannelsPage />, { permission: 'canManageIntegrations' })
              } />
              <Route path="agenda" element={
                crmPage("Calendar", <CalendarPage />, { permission: 'canViewCalendar' })
              } />
              <Route path="tarefas" element={
                crmPage("Tasks", <TasksPage />, { permission: 'canViewTasks' })
              } />
              <Route path="clientes" element={
                crmPage("Customers", <Customers />, { permission: 'canViewCustomers' })
              } />
              <Route path="clientes/:id" element={
                crmPage("Customer Details", <CustomerDetails />, { permission: 'canViewCustomers' })
              } />
              {/* Story 08 — pessoas. Nao existe `services/contacts.ts`: a
                  lista e' uma projecao sobre `GET /crm/leads` (decisao R1 da
                  spec). O gate repete o do item do painel. */}
              <Route path="contatos" element={
                crmPage("Contacts", <ContactsPage />, { permission: 'canViewContacts' })
              } />
              {/* Story 08 — importar em tela cheia. A tela escolhe o destino
                  entre os que o usuario pode CRIAR; o dialogo de Leads e
                  Clientes continua existindo, sobre os mesmos passos. */}
              <Route path="importar" element={
                crmPage("Import", <ImportPage />, { permission: 'canViewLeads' })
              } />
              <Route path="produtos" element={
                crmPage("Products", <Products />, { permission: 'canViewProducts' })
              } />
              <Route path="produtos/:id" element={
                crmPage("Product Details", <ProductDetails />, { permission: 'canViewProducts' })
              } />
              <Route path="contratos" element={
                crmPage("Expiring Contracts", <ExpiringContractsPage />, { permission: 'canViewContracts' })
              } />
              <Route path="agentes-voz" element={
                crmPage("Voice Agents", <VoiceAgents />, { feature: 'crm_show_voice_agents', permission: 'canManageVoiceAgents' })
              } />
              <Route path="agentes-voz/:id" element={
                crmPage("Voice Config Edit", <VoiceConfigEditPage />, { feature: 'crm_show_voice_agents', permission: 'canManageVoiceAgents' })
              } />
              <Route path="voip" element={
                crmPage("Call Center", <CallCenter />, { feature: 'crm_show_voip', permission: 'canCreateActivities' })
              } />
              <Route path="numeros-telefone" element={
                crmPage("Phone Numbers", <PhoneNumbers />, { feature: 'crm_show_phone_numbers', permission: 'canManageIntegrations' })
              } />
              <Route path="historico-ligacoes" element={
                crmPage("Call History", <CallHistory />, { feature: 'crm_show_voice_agents', permission: 'canViewActivities' })
              } />
              <Route path="tokens" element={
                crmPage("Tokens", <TokensPage />)
              } />
              <Route path="uso" element={
                crmPage("Usage", <UsagePage />)
              } />
              <Route path="relatorios" element={
                crmPage("Reports", <Reports />, { permission: 'canAccessReports' })
              } />
              {/* Story 11 (redesign-crm) — as tres sub-visoes de Relatorios.
                  NAO sao consultas novas: `GET /crm/stats/reports` ja devolve
                  funil, desempenho por SDR e canais na mesma resposta. O
                  endereco proprio existe para cada visao caber no painel do
                  hub e sobreviver a recarga; o gate e' o MESMO da raiz,
                  porque e' a mesma rota com um parametro. */}
              <Route path="relatorios/:view" element={
                crmPage("Reports", <Reports />, { permission: 'canAccessReports' })
              } />
              <Route path="analytics" element={
                crmPage("Analytics", <AnalyticsPage />, { permission: 'canViewDashboard' })
              } />
              <Route path="credenciais" element={
                crmPage("Credentials", <CredentialsPage />, { permission: 'canManageIntegrations' })
              } />
              <Route path="webhooks" element={
                crmPage("Outbound Webhooks", <WebhooksPage />, { permission: 'canManageIntegrations' })
              } />
              <Route path="auditoria" element={
                crmPage("Audit Logs", <AuditLogsPage />, { permission: 'canViewAuditLogs' })
              } />
              <Route path="notificacoes" element={
                crmPage("Notifications", <NotificationsPage />)
              } />
              <Route path="equipe" element={
                crmPage("Team", <TeamPage />, { permission: 'canManageTeam' })
              } />
              <Route path="equipe/cargos" element={
                crmPage("Roles", <RolesPage />, { permission: 'canManageTeam' })
              } />
              <Route path="equipe/:id" element={
                crmPage("Member Detail", <MemberDetailPage />, { permission: 'canManageTeam' })
              } />
              <Route path="configuracoes" element={
                crmPage("Settings", <Settings />, { permission: 'canManageSettings' })
              } />
              {/* Story 11 (redesign-crm) — alias de secao. A MESMA tela e o
                  MESMO gate: o que muda e' que cada secao passa a ter
                  endereco proprio, entao o item do painel do hub fica ativo
                  e o link sobrevive a recarga. `?section=` continua valendo
                  (e2e/tests/helpers/crm.ts e links ja enviados). */}
              <Route path="configuracoes/:section" element={
                crmPage("Settings", <Settings />, { permission: 'canManageSettings' })
              } />
              <Route path="perfil" element={
                crmPage("Profile", <Profile />)
              } />
              <Route path="subscription" element={
                crmPage("Subscription", <SubscriptionPage />)
              } />
            </Route>


            <Route path="*" element={publicPage("Not Found", <NotFound />)} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
